/**
 * Split a polygon into connected regions reachable by a path of at
 * least `minWidth` (default 3'). Used to detect cases where a Voronoi-
 * clipped section polygon contains an "island" of area that's only
 * accessible through a < 3' pinch — those islands aren't really
 * reachable as part of the original section and should be split off.
 *
 * Approach: grid flood-fill (approach B from the design doc).
 *   1. Sample the polygon on a fixed grid (default 6").
 *   2. Mark a cell "reachable" iff its center is inside the polygon
 *      AND ≥ minWidth/2 from every polygon edge.
 *   3. Flood-fill (4-connectivity) the reachable cells into connected
 *      components.
 *   4. Trace each component's outer cell boundary into a polygon and
 *      simplify collinear edges.
 *
 * Output polygons are CW math (negative shoelace) to match the
 * project's "CCW from above" floor-polygon convention.
 *
 * Pure: takes data in, returns geometry. No external deps.
 */

// Hoisted from the GLOBAL `Math` so hot loops below reference a module-scope
// binding. A global lookup is not inline-cacheable inside a `vm` context
// (how jest isolates modules), which made these loops ~100x slower under
// test than in production. Exactly semantics-preserving: every `Math`
// member is a pure static that never reads `this`.
const MATH_ABS = Math.abs;
const MATH_CEIL = Math.ceil;
const MATH_FLOOR = Math.floor;
const { hypot2 } = require('./hypot2');
const MATH_MAX = Math.max;
const MATH_MIN = Math.min;
const MATH_SQRT = Math.sqrt;

const FOOT = 0.3048;
const DEFAULT_MIN_WIDTH = 3 * FOOT;
const DEFAULT_GRID_SIZE = 0.15; // 6"

function distancePointToSegment2D(p, a, b) {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len2 = dx * dx + dz * dz;
  if (len2 === 0) return hypot2(p.x - a.x, p.z - a.z);
  let t = ((p.x - a.x) * dx + (p.z - a.z) * dz) / len2;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  return hypot2(p.x - (a.x + t * dx), p.z - (a.z + t * dz));
}

const { isPointInsidePolygon } = require('./pointInPolygon');

/**
 * Proper segment-segment crossing (segments strictly cross each other,
 * not just touch at endpoints). Used to detect when a probe segment
 * pierces a polygon edge — i.e., a wall or section boundary lies
 * between the two endpoints.
 */
function segmentsCross(p1, p2, p3, p4) {
  const ccw = (a, b, c) =>
    (b.x - a.x) * (c.z - a.z) - (b.z - a.z) * (c.x - a.x);
  const d1 = ccw(p3, p4, p1);
  const d2 = ccw(p3, p4, p2);
  const d3 = ccw(p1, p2, p3);
  const d4 = ccw(p1, p2, p4);
  return (
    ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
  );
}

/**
 * True iff segment a→b stays inside `polygon`: both endpoints inside
 * AND no proper crossing of any polygon edge. Strict containment —
 * floating-point boundary cases will reject. Callers that need
 * boundary-tolerance should use `segmentReachesInside` instead.
 */
function segmentInsidePolygon(a, b, polygon) {
  if (!isPointInsidePolygon(a, polygon)) return false;
  if (!isPointInsidePolygon(b, polygon)) return false;
  for (let i = 0; i < polygon.length; i++) {
    const p3 = polygon[i];
    const p4 = polygon[(i + 1) % polygon.length];
    if (segmentsCross(a, b, p3, p4)) return false;
  }
  return true;
}

/**
 * Like segmentInsidePolygon but tolerates `a` lying exactly on the
 * polygon boundary (common for bisector anchors which sit on a
 * section's edge). Requires:
 *   - `b` strictly inside (the trusted in-polygon endpoint)
 *   - the open segment (a, b) — sampled at 10 interior points —
 *     stays inside the polygon end-to-end. We skip `a` itself because
 *     point-in-polygon is a coin-flip on the boundary, but we need
 *     EVERY interior sample to test inside (otherwise the segment
 *     exits the polygon mid-way, e.g. through a wall, and re-enters).
 *
 * The interior-sample approach replaces the proper-crossing test for
 * polygon edges, which is brittle when `a` sits exactly on an edge:
 * floating-point pushes `a` ε to one side, the cross check fires for
 * that edge, and we incorrectly reject a segment that actually heads
 * straight into the polygon interior.
 */
function segmentReachesInside(a, b, polygon) {
  if (!isPointInsidePolygon(b, polygon)) return false;
  const N = 10;
  for (let i = 0; i < N; i++) {
    const t = (i + 0.5) / N;
    const p = { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t };
    if (!isPointInsidePolygon(p, polygon)) return false;
  }
  return true;
}

/**
 * Trace the outer boundary of a connected cell set (4-connectivity)
 * into a CW-math polygon. Each cell side adjacent to a non-set cell
 * (or off-grid) becomes a boundary edge in the canonical CW direction;
 * connecting them by start/end vertex yields the polygon.
 */
function cellsToPolygon(cells, gridSize, originX, originZ) {
  const set = new Set();
  for (const [r, c] of cells) set.add(`${r},${c}`);

  const edges = [];
  for (const [r, c] of cells) {
    const x0 = originX + c * gridSize;
    const z0 = originZ + r * gridSize;
    const x1 = x0 + gridSize;
    const z1 = z0 + gridSize;
    // Top side (neighbor at r-1, c is outside): going -x at z=z0
    if (!set.has(`${r - 1},${c}`)) {
      edges.push([
        { x: x1, z: z0 },
        { x: x0, z: z0 },
      ]);
    }
    // Right side (r, c+1 outside): going -z at x=x1
    if (!set.has(`${r},${c + 1}`)) {
      edges.push([
        { x: x1, z: z1 },
        { x: x1, z: z0 },
      ]);
    }
    // Bottom side (r+1, c outside): going +x at z=z1
    if (!set.has(`${r + 1},${c}`)) {
      edges.push([
        { x: x0, z: z1 },
        { x: x1, z: z1 },
      ]);
    }
    // Left side (r, c-1 outside): going +z at x=x0
    if (!set.has(`${r},${c - 1}`)) {
      edges.push([
        { x: x0, z: z0 },
        { x: x0, z: z1 },
      ]);
    }
  }
  if (edges.length === 0) return [];

  const fix = (n) => n.toFixed(6);
  const startMap = new Map();
  for (const e of edges) startMap.set(`${fix(e[0].x)},${fix(e[0].z)}`, e);

  // Pick an outer-boundary start: the leftmost (then bottom-most) vertex.
  let start = edges[0];
  for (const e of edges) {
    if (e[0].x < start[0].x || (e[0].x === start[0].x && e[0].z < start[0].z)) {
      start = e;
    }
  }

  const polygon = [];
  let current = start;
  let safety = edges.length + 1;
  while (safety-- > 0) {
    polygon.push(current[0]);
    const next = startMap.get(`${fix(current[1].x)},${fix(current[1].z)}`);
    if (!next || next === start) break;
    current = next;
  }
  return simplifyCollinear(polygon);
}

function simplifyCollinear(poly, eps = 1e-9) {
  if (poly.length < 3) return poly;
  const out = [];
  for (let i = 0; i < poly.length; i++) {
    const prev = poly[(i - 1 + poly.length) % poly.length];
    const curr = poly[i];
    const next = poly[(i + 1) % poly.length];
    const cross =
      (curr.x - prev.x) * (next.z - curr.z) -
      (curr.z - prev.z) * (next.x - curr.x);
    if (MATH_ABS(cross) > eps) out.push(curr);
  }
  return out;
}

function polygonArea(poly) {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const p1 = poly[i];
    const p2 = poly[(i + 1) % poly.length];
    a += p1.x * p2.z - p2.x * p1.z;
  }
  return MATH_ABS(a / 2);
}

/**
 * @param {Array<{x, z}>} polygon
 * @param {{minWidth?: number, gridSize?: number}} [options]
 * @returns {Array<Array<{x, z}>>}  one polygon per reachable region,
 *   sorted largest-first by area. Empty if the polygon has no area
 *   reachable at the requested minWidth.
 */
function reachableRegions(polygon, options = {}) {
  const minWidth = options.minWidth ?? DEFAULT_MIN_WIDTH;
  const gridSize = options.gridSize ?? DEFAULT_GRID_SIZE;
  if (!polygon || polygon.length < 3) return [];

  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  for (const p of polygon) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  minX -= gridSize;
  minZ -= gridSize;
  maxX += gridSize;
  maxZ += gridSize;

  const cols = MATH_CEIL((maxX - minX) / gridSize) + 1;
  const rows = MATH_CEIL((maxZ - minZ) / gridSize) + 1;
  const halfWidth = minWidth / 2;

  const grid = [];
  for (let r = 0; r < rows; r++) {
    grid[r] = new Uint8Array(cols);
    for (let c = 0; c < cols; c++) {
      const x = minX + c * gridSize + gridSize / 2;
      const z = minZ + r * gridSize + gridSize / 2;
      if (!isPointInsidePolygon({ x, z }, polygon)) continue;
      let minD = Infinity;
      for (let i = 0; i < polygon.length; i++) {
        const a = polygon[i];
        const b = polygon[(i + 1) % polygon.length];
        const d = distancePointToSegment2D({ x, z }, a, b);
        if (d < minD) minD = d;
      }
      if (minD >= halfWidth) grid[r][c] = 1;
    }
  }

  const visited = [];
  for (let r = 0; r < rows; r++) visited[r] = new Uint8Array(cols);
  const components = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (!grid[r][c] || visited[r][c]) continue;
      const cells = [];
      const queue = [[r, c]];
      visited[r][c] = 1;
      while (queue.length) {
        const [cr, cc] = queue.shift();
        cells.push([cr, cc]);
        const ns = [
          [cr - 1, cc],
          [cr + 1, cc],
          [cr, cc - 1],
          [cr, cc + 1],
        ];
        for (const [nr, nc] of ns) {
          if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
          if (visited[nr][nc] || !grid[nr][nc]) continue;
          visited[nr][nc] = 1;
          queue.push([nr, nc]);
        }
      }
      components.push(cells);
    }
  }

  const polygons = components
    .map((cells) => cellsToPolygon(cells, gridSize, minX, minZ))
    .filter((p) => p.length >= 3);
  polygons.sort((a, b) => polygonArea(b) - polygonArea(a));
  return polygons;
}

/**
 * For a polygon split into multiple reachable components by < minWidth
 * pinches, compute a separator LINE for each pair of components — the
 * line where the geodesic Voronoi assignment flips between those two
 * components. The separator is approximated as the principal axis
 * through the midpoints of grid edges that cross the assignment
 * boundary.
 *
 * Returns an array of `{ start, end, componentA, componentB }`. Empty
 * if the polygon has fewer than two reachable components.
 */
function componentSeparators(polygon, options = {}) {
  const minWidth = options.minWidth ?? DEFAULT_MIN_WIDTH;
  const gridSize = options.gridSize ?? DEFAULT_GRID_SIZE;
  if (!polygon || polygon.length < 3) return [];

  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  for (const p of polygon) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  minX -= gridSize;
  minZ -= gridSize;
  maxX += gridSize;
  maxZ += gridSize;
  const cols = MATH_CEIL((maxX - minX) / gridSize) + 1;
  const rows = MATH_CEIL((maxZ - minZ) / gridSize) + 1;
  const halfWidth = minWidth / 2;

  // Mark cells: 0 = outside polygon, 1 = in polygon (both navigable
  // and boundary cells share this slot for separator purposes).
  // Track a separate "navigable" flag for the seed step.
  const inPoly = [];
  const navigable = [];
  for (let r = 0; r < rows; r++) {
    inPoly[r] = new Uint8Array(cols);
    navigable[r] = new Uint8Array(cols);
    for (let c = 0; c < cols; c++) {
      const x = minX + c * gridSize + gridSize / 2;
      const z = minZ + r * gridSize + gridSize / 2;
      if (!isPointInsidePolygon({ x, z }, polygon)) continue;
      inPoly[r][c] = 1;
      let minD = Infinity;
      for (let i = 0; i < polygon.length; i++) {
        const a = polygon[i];
        const b = polygon[(i + 1) % polygon.length];
        const d = distancePointToSegment2D({ x, z }, a, b);
        if (d < minD) minD = d;
      }
      if (minD >= halfWidth) navigable[r][c] = 1;
    }
  }

  // Phase 1: flood-fill navigable cells into components (same as
  // reachableRegions).
  const owner = [];
  for (let r = 0; r < rows; r++) owner[r] = new Int16Array(cols).fill(-1);
  let nextComp = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (!navigable[r][c] || owner[r][c] !== -1) continue;
      const idx = nextComp++;
      const queue = [[r, c]];
      owner[r][c] = idx;
      while (queue.length) {
        const [cr, cc] = queue.shift();
        const ns = [
          [cr - 1, cc],
          [cr + 1, cc],
          [cr, cc - 1],
          [cr, cc + 1],
        ];
        for (const [nr, nc] of ns) {
          if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
          if (!navigable[nr][nc] || owner[nr][nc] !== -1) continue;
          owner[nr][nc] = idx;
          queue.push([nr, nc]);
        }
      }
    }
  }
  if (nextComp < 2) return [];

  // Phase 2: BFS from navigable cells outward to claim every in-polygon
  // cell for the closest component. Assignment boundary defines the
  // separator.
  const seedQueue = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (owner[r][c] !== -1) seedQueue.push([r, c]);
    }
  }
  while (seedQueue.length) {
    const [cr, cc] = seedQueue.shift();
    const idx = owner[cr][cc];
    const ns = [
      [cr - 1, cc],
      [cr + 1, cc],
      [cr, cc - 1],
      [cr, cc + 1],
    ];
    for (const [nr, nc] of ns) {
      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
      if (!inPoly[nr][nc] || owner[nr][nc] !== -1) continue;
      owner[nr][nc] = idx;
      seedQueue.push([nr, nc]);
    }
  }

  // Phase 3: collect grid edges where neighboring cells have different
  // owners. Group by (ownerA, ownerB) pair.
  const byPair = new Map();
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const a = owner[r][c];
      if (a < 0) continue;
      // Right neighbor
      if (c + 1 < cols) {
        const b = owner[r][c + 1];
        if (b >= 0 && b !== a) {
          const ka = MATH_MIN(a, b);
          const kb = MATH_MAX(a, b);
          const key = `${ka},${kb}`;
          if (!byPair.has(key)) byPair.set(key, []);
          // Vertical edge at x = minX + (c+1)*gridSize, z in [r, r+1]*gs
          const x = minX + (c + 1) * gridSize;
          const z0 = minZ + r * gridSize;
          byPair.get(key).push({
            mid: { x, z: z0 + gridSize / 2 },
          });
        }
      }
      // Bottom neighbor
      if (r + 1 < rows) {
        const b = owner[r + 1][c];
        if (b >= 0 && b !== a) {
          const ka = MATH_MIN(a, b);
          const kb = MATH_MAX(a, b);
          const key = `${ka},${kb}`;
          if (!byPair.has(key)) byPair.set(key, []);
          const z = minZ + (r + 1) * gridSize;
          const x0 = minX + c * gridSize;
          byPair.get(key).push({
            mid: { x: x0 + gridSize / 2, z },
          });
        }
      }
    }
  }

  // Phase 3b: for any component pair NOT in byPair (zero-width pinch:
  // the components are truly disconnected at the cell level), fall back
  // to the closest cell-center pair between them. The separator is a
  // short line crossing the gap between the two nearest in-polygon cells
  // — which is exactly where the pinch happens.
  const cellsByOwner = new Map();
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const o = owner[r][c];
      if (o < 0) continue;
      if (!cellsByOwner.has(o)) cellsByOwner.set(o, []);
      cellsByOwner.get(o).push({
        x: minX + c * gridSize + gridSize / 2,
        z: minZ + r * gridSize + gridSize / 2,
      });
    }
  }
  for (let a = 0; a < nextComp; a++) {
    for (let b = a + 1; b < nextComp; b++) {
      const key = `${a},${b}`;
      if (byPair.has(key)) continue;
      const cellsA = cellsByOwner.get(a) || [];
      const cellsB = cellsByOwner.get(b) || [];
      if (cellsA.length === 0 || cellsB.length === 0) continue;
      let bestD2 = Infinity;
      let bestPa = null;
      let bestPb = null;
      for (const pa of cellsA) {
        for (const pb of cellsB) {
          const d2 = (pa.x - pb.x) ** 2 + (pa.z - pb.z) ** 2;
          if (d2 < bestD2) {
            bestD2 = d2;
            bestPa = pa;
            bestPb = pb;
          }
        }
      }
      if (!bestPa || !bestPb) continue;
      // Use the perpendicular bisector of the closest pair as the
      // separator, with extent = the cell pair's distance (so the line
      // visibly crosses the gap rather than running along it).
      const mx = (bestPa.x + bestPb.x) / 2;
      const mz = (bestPa.z + bestPb.z) / 2;
      const dx = bestPb.x - bestPa.x;
      const dz = bestPb.z - bestPa.z;
      const dlen = hypot2(dx, dz);
      if (dlen < 1e-9) continue;
      // Perpendicular unit vector.
      const px = -dz / dlen;
      const pz = dx / dlen;
      const half = dlen / 2 + gridSize;
      byPair.set(key, [
        { mid: { x: mx + px * half, z: mz + pz * half } },
        { mid: { x: mx - px * half, z: mz - pz * half } },
      ]);
    }
  }

  // Phase 4: for each pair, fit a line via PCA on the edge midpoints
  // and clip to the projected extent.
  const separators = [];
  for (const [key, edges] of byPair) {
    const [ownerA, ownerB] = key.split(',').map(Number);
    if (edges.length === 0) continue;
    let cx = 0;
    let cz = 0;
    for (const e of edges) {
      cx += e.mid.x;
      cz += e.mid.z;
    }
    cx /= edges.length;
    cz /= edges.length;

    let sxx = 0;
    let sxz = 0;
    let szz = 0;
    for (const e of edges) {
      const dx = e.mid.x - cx;
      const dz = e.mid.z - cz;
      sxx += dx * dx;
      sxz += dx * dz;
      szz += dz * dz;
    }
    // Larger eigenvalue's eigenvector = principal axis.
    const trace = sxx + szz;
    const det = sxx * szz - sxz * sxz;
    const disc = MATH_SQRT(MATH_MAX(0, (trace * trace) / 4 - det));
    const lambda1 = trace / 2 + disc;
    let vx;
    let vz;
    if (MATH_ABS(sxz) > 1e-9) {
      vx = lambda1 - szz;
      vz = sxz;
    } else {
      vx = sxx >= szz ? 1 : 0;
      vz = sxx >= szz ? 0 : 1;
    }
    const vlen = hypot2(vx, vz);
    if (vlen < 1e-9) continue;
    vx /= vlen;
    vz /= vlen;

    let minProj = Infinity;
    let maxProj = -Infinity;
    for (const e of edges) {
      const p = (e.mid.x - cx) * vx + (e.mid.z - cz) * vz;
      if (p < minProj) minProj = p;
      if (p > maxProj) maxProj = p;
    }
    separators.push({
      start: { x: cx + minProj * vx, z: cz + minProj * vz },
      end: { x: cx + maxProj * vx, z: cz + maxProj * vz },
      componentA: ownerA,
      componentB: ownerB,
    });
  }
  return separators;
}

/**
 * The floor of `polygon` on a grid of `gridSize` squares: `inPoly` — floor
 * (inside the polygon, outside every obstacle) — and `navigable` — floor at
 * least `minWidth / 2` from every edge, where someone `minWidth` wide can
 * stand. Shared by `buildReachabilityMap` and `trappedFloor.js`; null for a
 * polygon of fewer than 3 points.
 */
function buildFloorGrid(polygon, options = {}) {
  const minWidth = options.minWidth ?? DEFAULT_MIN_WIDTH;
  const gridSize = options.gridSize ?? DEFAULT_GRID_SIZE;
  const obstacles = options.obstacles || [];
  if (!polygon || polygon.length < 3) return null;

  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  for (const p of polygon) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  minX -= gridSize;
  minZ -= gridSize;
  maxX += gridSize;
  maxZ += gridSize;
  const cols = MATH_CEIL((maxX - minX) / gridSize) + 1;
  const rows = MATH_CEIL((maxZ - minZ) / gridSize) + 1;
  const halfWidth = minWidth / 2;

  const inPoly = [];
  const navigable = [];
  for (let r = 0; r < rows; r++) {
    inPoly[r] = new Uint8Array(cols);
    navigable[r] = new Uint8Array(cols);
    for (let c = 0; c < cols; c++) {
      const x = minX + c * gridSize + gridSize / 2;
      const z = minZ + r * gridSize + gridSize / 2;
      if (!isPointInsidePolygon({ x, z }, polygon)) continue;
      // A cell inside an obstacle is solid, not floor — leave inPoly=0 so it
      // blocks passage exactly like the region exterior.
      let inObstacle = false;
      for (const obs of obstacles) {
        if (isPointInsidePolygon({ x, z }, obs)) {
          inObstacle = true;
          break;
        }
      }
      if (inObstacle) continue;
      inPoly[r][c] = 1;
      let minD = Infinity;
      for (let i = 0; i < polygon.length; i++) {
        const a = polygon[i];
        const b = polygon[(i + 1) % polygon.length];
        const d = distancePointToSegment2D({ x, z }, a, b);
        if (d < minD) minD = d;
      }
      for (const obs of obstacles) {
        for (let i = 0; i < obs.length; i++) {
          const d = distancePointToSegment2D(
            { x, z },
            obs[i],
            obs[(i + 1) % obs.length]
          );
          if (d < minD) minD = d;
        }
      }
      if (minD >= halfWidth) navigable[r][c] = 1;
    }
  }
  return { minX, minZ, rows, cols, gridSize, inPoly, navigable };
}

/**
 * Build a reachability classifier for a polygon.
 *
 * Returns `classify(point)` → area-sorted component index (0 = main =
 * largest reachable component, 1..N-1 = isolated), or -1 if `point` is
 * not in any classified cell.
 *
 * Uses the same grid setup as `componentSeparators`: navigable cells
 * (≥ minWidth/2 from any edge) are flooded into components, then a
 * second BFS expands ownership outward to every in-polygon cell so
 * boundary points can be classified too.
 *
 * Component indices are area-sorted (by cell count) DESCENDING so they
 * match `reachableRegions(...)` indexing.
 *
 * Two per-component areas come back, both indexed the way `classify`
 * indexes: `componentAreas` counts only the navigable (minWidth-eroded)
 * core, `componentFloorAreas` counts every floor cell the component owns
 * after phase 2. Use the latter to answer "how much FLOOR is on this side
 * of the pinch" — the eroded core shrinks with minWidth and understates a
 * real pocket badly at wide probe widths.
 *
 * `options.obstacles` (array of polygons) are treated as solid, non-walkable
 * fills INSIDE the outer polygon — e.g. cabinet peninsulas. A cell inside an
 * obstacle is not floor (blocks passage like the polygon exterior), and every
 * cell keeps the same `minWidth/2` clearance from obstacle edges as from the
 * outer wall. With no obstacles the behavior is identical to before.
 */
function buildReachabilityMap(polygon, options = {}) {
  const empty = { classify: () => -1, nComponents: 0 };
  const grid = buildFloorGrid(polygon, options);
  if (!grid) return empty;
  const { minX, minZ, rows, cols, gridSize, inPoly, navigable } = grid;

  // Phase 1: flood navigable cells into scan-order components.
  const owner = [];
  for (let r = 0; r < rows; r++) owner[r] = new Int16Array(cols).fill(-1);
  let nextComp = 0;
  const componentCellCounts = [];
  const componentFirstCell = []; // scanIdx → [r, c] of a cell in the component
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (!navigable[r][c] || owner[r][c] !== -1) continue;
      const idx = nextComp++;
      componentFirstCell.push([r, c]);
      let count = 0;
      const queue = [[r, c]];
      owner[r][c] = idx;
      while (queue.length) {
        const [cr, cc] = queue.shift();
        count += 1;
        const ns = [
          [cr - 1, cc],
          [cr + 1, cc],
          [cr, cc - 1],
          [cr, cc + 1],
        ];
        for (const [nr, nc] of ns) {
          if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
          if (!navigable[nr][nc] || owner[nr][nc] !== -1) continue;
          owner[nr][nc] = idx;
          queue.push([nr, nc]);
        }
      }
      componentCellCounts.push(count);
    }
  }
  if (nextComp === 0) return empty;

  // Phase 2: BFS outward to claim every in-polygon cell. Each component's
  // FLOOR area (every cell it owns) is tracked alongside its NAVIGABLE area
  // (the minWidth-eroded core from phase 1): a caller asking "how much floor
  // did this seal off" wants the former — at a 48" minWidth the eroded core
  // of a 46 sq ft sealed box measures under 7.
  const componentFloorCellCounts = componentCellCounts.slice();
  const seedQueue = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (owner[r][c] !== -1) seedQueue.push([r, c]);
    }
  }
  while (seedQueue.length) {
    const [cr, cc] = seedQueue.shift();
    const idx = owner[cr][cc];
    const ns = [
      [cr - 1, cc],
      [cr + 1, cc],
      [cr, cc - 1],
      [cr, cc + 1],
    ];
    for (const [nr, nc] of ns) {
      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
      if (!inPoly[nr][nc] || owner[nr][nc] !== -1) continue;
      owner[nr][nc] = idx;
      componentFloorCellCounts[idx] += 1;
      seedQueue.push([nr, nc]);
    }
  }

  // Build scan-order → area-order mapping (largest first).
  const order = componentCellCounts
    .map((count, scanIdx) => ({ scanIdx, count }))
    .sort((a, b) => b.count - a.count);
  const scanToArea = new Int16Array(nextComp);
  for (let i = 0; i < order.length; i++) scanToArea[order[i].scanIdx] = i;

  // Per-component area (m²) and a representative interior point, both indexed
  // by the SAME area-sorted index `classify` returns.
  const cellArea = gridSize * gridSize;
  const componentAreas = new Array(nextComp);
  const componentFloorAreas = new Array(nextComp);
  const componentSamples = new Array(nextComp);
  for (const { scanIdx, count } of order) {
    const areaIdx = scanToArea[scanIdx];
    componentAreas[areaIdx] = count * cellArea;
    componentFloorAreas[areaIdx] = componentFloorCellCounts[scanIdx] * cellArea;
    const [fr, fc] = componentFirstCell[scanIdx];
    componentSamples[areaIdx] = {
      x: minX + fc * gridSize + gridSize / 2,
      z: minZ + fr * gridSize + gridSize / 2,
    };
  }

  function classify(point) {
    if (!point) return -1;
    const c = MATH_FLOOR((point.x - minX) / gridSize);
    const r = MATH_FLOOR((point.z - minZ) / gridSize);
    if (r < 0 || r >= rows || c < 0 || c >= cols) return -1;
    const o = owner[r][c];
    if (o >= 0) return scanToArea[o];
    // Direct cell unowned (cell-center fell outside the polygon while
    // `point` may still sit on or just inside the polygon edge —
    // common for bisector anchors). Find the nearest neighbor cell
    // whose owned center is reachable by a segment that doesn't cross
    // any polygon edge AND whose 1%-toward-neighbor probe lands inside
    // the polygon. Both conditions must hold so we can't peek across a
    // wall or report an answer for a point genuinely outside.
    let bestOwner = -1;
    let bestDist2 = Infinity;
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;
        const nr = r + dr;
        const nc = c + dc;
        if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
        const no = owner[nr][nc];
        if (no < 0) continue;
        const cx = minX + nc * gridSize + gridSize / 2;
        const cz = minZ + nr * gridSize + gridSize / 2;
        const dx = point.x - cx;
        const dz = point.z - cz;
        const d2 = dx * dx + dz * dz;
        if (d2 >= bestDist2) continue;
        if (!segmentReachesInside(point, { x: cx, z: cz }, polygon)) continue;
        bestOwner = no;
        bestDist2 = d2;
      }
    }
    return bestOwner < 0 ? -1 : scanToArea[bestOwner];
  }

  return {
    classify,
    nComponents: nextComp,
    componentAreas,
    componentFloorAreas,
    componentSamples,
  };
}

module.exports = {
  buildFloorGrid,
  reachableRegions,
  componentSeparators,
  buildReachabilityMap,
  polygonArea,
  cellsToPolygon,
  isPointInsidePolygon,
  DEFAULT_MIN_WIDTH,
  DEFAULT_GRID_SIZE,
};
